import React, {useMemo} from 'react';
import styled, {css} from 'styled-components';

import ContentWrapper from 'components/contentWrapper';

import theme, {bp, color} from 'settings/theme';


type Props = {
  product: any
};


let specTypes = {
  'weight_kg': 'weight',
  'overall_width_cm': 'width',
  'width_cm': 'width',
  'max_overall_width_cm': 'width',
  'max_width_cm': 'width',
  'overall_length_cm': 'depth',
  'length_cm': 'depth',
  'max_overall_length_cm': 'depth',
  'max_length_cm': 'depth',
  'max_height_cm': 'depth'
};

function convertSpec(key, value) {

  const allowedUnits = ['cm','kg','miles','mph']

  let valueWithUnit = ''
  let valueWithUnit2 = ''

  // Split the key
  let keyArr = key.toLowerCase().split('_');

  // If there is a value and the last section of the key is a recognized unit
  if(keyArr.length > 1 && allowedUnits.includes(keyArr[keyArr.length - 1])){

    let units = keyArr[keyArr.length - 1];

    if(value){

      valueWithUnit = value ? `${value}${units}` : value;

      if(units === 'cm'){
        valueWithUnit2 = (value/2.54).toFixed(2) + 'in' ;
      }

      if(units === 'kg'){
        let lbs = 2.20462 * value;
        valueWithUnit2 = ` ${Math.floor(lbs / 14)}st ${(lbs % 14).toFixed(0)}lb`;
      }

      if(units === 'miles'){
        valueWithUnit2 = (value * 1.60934).toFixed(2) + 'km' ;
      }

      if(units === 'mph'){
        valueWithUnit2 = (value * 1.60934).toFixed(2) + 'km/h' ;
      }

    }
    
    keyArr.pop();

  }
  else if(Array.isArray(value)){
    valueWithUnit = value.join(', ');
  }
  else if(value === true){
    valueWithUnit = 'Yes';
  }
  else if(value === false){
    valueWithUnit = 'No';
  }
  else{
    valueWithUnit = value;
  }

  keyArr = keyArr.map(item => item.charAt(0).toUpperCase() + item.slice(1));

  return {
    key: keyArr.join(' '),
    value,
    valueWithUnit,
    valueWithUnit2,
    specType: null
  };
  
}


// Product Header
const Component: React.FC<Props> = (props) => {

  // Specs
  const specs = useMemo(() => {

      let specs: any[] = [];

      if(props.product?.content?.spec_sheet && Array.isArray(props.product?.content?.spec_sheet)){

        props.product?.content?.spec_sheet.forEach(sheet => {

          for (const [ key, value ] of Object.entries(sheet)) {
              
            if (!key.startsWith('_') && key !== 'component'){

              const convertedSpec: any = convertSpec(key, value);

              let keys = Object.keys(specTypes);

              if(keys.includes(key)){
                convertedSpec.specType = specTypes[key];
              }

              specs.push(convertedSpec);
              
            }

          }

        });
  
      }

      return specs;

  }, [props.product?.content?.spec_sheet]);


  // Additional Specs
  const additionalSpecs = useMemo(() => {

    let specs: any[] = [];

    if(props.product?.content?.additional_specs?.length > 0){

      specs = props.product?.content?.additional_specs.split(/\r?\n/);

      specs = specs.map(item => {

        const spec = item.split(':');
        const key = spec[0].trim();
        const value = spec[1].trim();

        return {key, value};

      });

    }

    return specs;

  }, [props.product?.content?.additional_specs]);
  

  return (
    <StyledComponent className="productSpecs spacingTop">
        
      {/* Intro */}
      <div className="spacingBottomHalf">
        <ContentWrapper>
          <h3 className="spacingBottomHalf">Product Specification</h3>
        </ContentWrapper>
      </div>


      {/* Specs */}
      <ContentWrapper fullWidthAt={[1,2]}>

        <div className="productSpecs-box paddingVertical">

          <ContentWrapper fullWidthAt={[3,4,5,6]}>

          {(specs.length > 0 || additionalSpecs.length > 0) && 

            <div className="productSpecs-specs">
              <>
                {specs.length > 0 && specs.map(spec => (

                  <React.Fragment key={spec.specType}>
                    {spec.value !== undefined && spec.value !== '' &&
                      <div className="productSpecs-spec">
                        <span>{spec.key}</span>
                        <span>
                          <span itemProp={spec.specType}>
                            {spec.valueWithUnit}
                          </span>
                          {spec.valueWithUnit2 && 
                            <span> / {spec.valueWithUnit2}</span>
                          }
                        </span>
                      </div>
                    }
                  </React.Fragment>

                ))}
                {additionalSpecs.length > 0 && additionalSpecs.map(spec => (

                  <div className="productSpecs-spec" key={spec.key}>
                    <span>{spec.key}</span>
                    <span>{spec.value}</span>
                  </div>

                ))}
              </>
            </div>

          }

          </ContentWrapper>

        </div>

      </ContentWrapper>

    </StyledComponent>
  );

}


export default Component;




// Styles
const StyledComponent = styled.div`

  display: block;

  .productSpecs-box{
    border-top: solid 1px ${color.lines};
    border-bottom: solid 1px ${color.lines};
    background-color: white;
  }

  .productSpecs-specs{
    display: flex;
    flex-wrap: wrap;
  }

  .productSpecs-spec{

    display: flex;
    width: 100%;
    padding-bottom: 0.8rem;

    span{

      width: 50%;
      font-size: 14px;

      &:first-child{
        max-width: 250px;
        text-transform: capitalize;
      }

      &:last-child{
        font-weight: bold;
      }

    }

  }


  @media (min-width: ${bp(3)}) {

    .productSpecs-box{
      padding-left: 30px;
      padding-right: 30px;
      border: solid 1px ${color.lines};
    }

  }

  @media (min-width: ${bp(4)}) {

    .productSpecs-specs{
      margin-left: -30px;
      margin-right: -30px;
    }

    .productSpecs-spec{
      width: 50%;
      padding-left: 30px;
      padding-right: 30px;
    }

  }


  @media (min-width: ${bp(5)}) {

    .productSpecs-specs{
      margin-left: -50px;
      margin-right: -50px;
    }

    .productSpecs-spec{
      width: 33.33333%;
      padding-left: 50px;
      padding-right: 50px;
    }

  }


`



